import { Service, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type {
  OnyxCoordinates,
  OnyxDropdownOptionsSource,
  OnyxOption,
  OnyxRouteLeg,
} from '@onyx/angular';
import {
  AddressHelper,
  OnyxFilterPipe,
  OnyxPluralTranslatePipe,
  OnyxRouteLegType,
} from '@onyx/angular';
import { chain, minBy } from 'lodash';
import { OrderPointType } from '../../dashboard/orders/common/enums/order-point-type';
import { FERRY_ROUTES } from '../constants/transit/ferry-routes';
import { TRAIN_ROUTES } from '../constants/transit/train-routes';
import { TUNNEL_ROUTES } from '../constants/transit/tunnel-routes';
import { TransitFrequencyInterval } from '../enums/transit/transit-frequency-interval';
import type { TransitType } from '../enums/transit/transit-type';
import type { TransitFrequency } from '../interfaces/transit/transit-frequency';
import type { TransitRoute } from '../interfaces/transit/transit-route';
import { DurationPipe } from '../pipes/duration.pipe';

@Service()
export class TransitHelper {
  private readonly filterPipe = inject(OnyxFilterPipe);
  private readonly durationPipe = inject(DurationPipe);
  private readonly translateService = inject(TranslateService);
  private readonly pluralTranslatePipe = inject(OnyxPluralTranslatePipe);

  public getRoutesSource(
    type: TransitType | 'all',
  ): OnyxDropdownOptionsSource<TransitRoute> {
    const routes = TransitHelper.getRoutes(type);

    const buildOption = (route: TransitRoute): OnyxOption<TransitRoute> => {
      const translateFrequency = (frequency: TransitFrequency): string => {
        const { interval, value } = frequency;

        let key = `frequency.${interval}`;
        if (interval !== TransitFrequencyInterval.MANY) {
          key = this.pluralTranslatePipe.transform(key, value);
        }

        return this.translateService.instant(key, { value });
      };

      return {
        name: `${route.from.name} (${route.from.address.countryCode.toUpperCase()}) → ${route.to.name} (${route.to.address.countryCode.toUpperCase()})`,
        value: route,
        leftFlag: route.from.address.countryCode,
        description: `${route.operator} · ${translateFrequency(route.frequency)}`,
        hint: this.durationPipe.transform(route.duration, {
          units: ['h'],
          singleUnit: true,
        })!,
        rightFlag: route.to.address.countryCode,
      };
    };

    return {
      list: (query, { page, limit }) => ({
        options: chain(routes)
          .map((route) => buildOption(route))
          .orderBy((route) => route.name)
          .thru((options) =>
            this.filterPipe
              .transform(options, query, ['name', 'description'])
              .slice((page - 1) * limit, page * limit),
          )
          .value(),
        totalItems: routes.length,
      }),
      get: (uuid) => {
        const route = routes.find((route) => route.uuid === uuid);
        return route ? buildOption(route) : null;
      },
      idKey: 'uuid',
    };
  }

  public static getRoutes(type: TransitType | 'all'): TransitRoute[] {
    return {
      [OrderPointType.FERRY]: FERRY_ROUTES,
      [OrderPointType.TRAIN]: TRAIN_ROUTES,
      [OrderPointType.TUNNEL]: TUNNEL_ROUTES,
      all: [...FERRY_ROUTES, ...TRAIN_ROUTES, ...TUNNEL_ROUTES],
    }[type];
  }

  public static fromCoordinates(
    departure: OnyxCoordinates,
    arrival: OnyxCoordinates,
  ): TransitRoute {
    const transitRoutes = TransitHelper.getRoutes('all');

    return minBy(
      transitRoutes,
      (transitRoute) =>
        AddressHelper.getHaversineDistance(
          departure,
          transitRoute.from.address,
        ) +
        AddressHelper.getHaversineDistance(arrival, transitRoute.to.address),
    )!;
  }

  public static fromRouteLeg(
    currentLeg: OnyxRouteLeg,
    previousLeg: OnyxRouteLeg | null,
  ): TransitRoute | null {
    if (!previousLeg || currentLeg.type === OnyxRouteLegType.VEHICLE) {
      return null;
    }

    return TransitHelper.fromCoordinates(
      previousLeg.arrival,
      currentLeg.arrival,
    );
  }
}
